import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress
} from '@mui/material';
import { Search, CheckCircle, Cancel } from '@mui/icons-material';
import api from '../../services/api';

const StyledCard = styled(Card)`
  margin-bottom: 20px;
  border-left: 4px solid ${props => props.$matched ? '#4caf50' : '#ff9800'};
`;

const MovieInfo = styled.div`
  margin-bottom: 15px;
`;

const SearchSection = styled.div`
  margin-top: 15px;
`;

const SearchInput = styled(TextField)`
  margin-right: 10px;
`;

const ResultsGrid = styled(Grid)`
  margin-top: 15px;
`;

const ResultCard = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
`;

const PosterImage = styled.img`
  width: 100%;
  border-radius: 4px;
  margin-bottom: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
`;

function MovieMatchCard({ movie, onMatch, onSkip }) {
  const [searchQuery, setSearchQuery] = useState(movie.title);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isMatched, setIsMatched] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const results = await api.searchTMDB(searchQuery, movie.year);
      setSearchResults(results.results || []);
    } catch (error) {
      console.error('Error searching TMDB:', error);
      alert('Failed to search movies. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectMovie = (tmdbMovie) => {
    setIsMatched(true);
    onMatch(movie, {
      tmdbId: tmdbMovie.id,
      tmdbTitle: tmdbMovie.title,
      posterPath: tmdbMovie.poster_path,
      releaseDate: tmdbMovie.release_date
    });
  };

  const handleSkipMovie = () => {
    onSkip(movie);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <StyledCard $matched={isMatched}>
      <CardContent>
        <MovieInfo>
          <Typography variant="h6">
            {movie.title} ({movie.year})
          </Typography>
          {movie.rating && (
            <Typography variant="body2" color="textSecondary">
              Rating: {movie.rating} stars
            </Typography>
          )}
          {isMatched && (
            <Typography variant="body2" style={{ color: '#4caf50', marginTop: '10px' }}>
              <CheckCircle style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '5px' }} />
              Matched!
            </Typography>
          )}
        </MovieInfo>

        {!isMatched && (
          <>
            <SearchSection>
              <Typography variant="body2" gutterBottom>
                Search for the correct movie:
              </Typography>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <SearchInput
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Search movie title"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={isSearching ? <CircularProgress size={20} color="inherit" /> : <Search />}
                  onClick={handleSearch}
                  disabled={isSearching}
                >
                  Search
                </Button>
              </div>
            </SearchSection>

            {searchResults.length > 0 && (
              <ResultsGrid container spacing={2}>
                {searchResults.slice(0, 6).map((result) => (
                  <Grid item xs={6} sm={4} md={2} key={result.id}>
                    <ResultCard onClick={() => handleSelectMovie(result)}>
                      {result.poster_path ? (
                        <PosterImage
                          src={`https://image.tmdb.org/t/p/w185${result.poster_path}`}
                          alt={result.title}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '150px',
                          backgroundColor: '#ddd',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          marginBottom: '8px'
                        }}>
                          <Typography variant="caption">No Poster</Typography>
                        </div>
                      )}
                      <Typography variant="caption" display="block">
                        {result.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {result.release_date ? result.release_date.split('-')[0] : 'N/A'}
                      </Typography>
                    </ResultCard>
                  </Grid>
                ))}
              </ResultsGrid>
            )}

            <ButtonGroup>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<Cancel />}
                onClick={handleSkipMovie}
              >
                Skip This Movie
              </Button>
            </ButtonGroup>
          </>
        )}
      </CardContent>
    </StyledCard>
  );
}

export default MovieMatchCard;
